import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, CheckCircle2, FileText, Building2, User, CreditCard, FolderOpen } from 'lucide-react';

interface DocUpload {
  label: string;
  field: string;
  required: boolean;
  description?: string;
}

const legalDocs: DocUpload[] = [
  { label: 'Business Registration Certificate', field: 'business_registration_url', required: true },
  { label: 'Commercial (Trade) License', field: 'trade_license_url', required: true, description: 'Renewed and valid' },
  { label: 'TIN Certificate', field: 'tin_certificate_url', required: true },
  { label: 'VAT Registration Certificate', field: 'vat_certificate_url', required: false, description: 'If applicable' },
];

const credentialDocs: DocUpload[] = [
  { label: 'Government-issued Real Estate Developer License', field: 'developer_license_url', required: false, description: 'Or qualification certificate' },
  { label: 'Letter of Active Involvement in Real Estate', field: 'involvement_letter_url', required: false },
];

const idDocs: DocUpload[] = [
  { label: 'ID or Passport of Owner(s)', field: 'id_document_url', required: true, description: 'Shareholders or authorized representatives' },
  { label: 'Power of Attorney', field: 'power_of_attorney_url', required: false, description: 'If someone applies on behalf of the company' },
];

const feeDocs: DocUpload[] = [
  { label: 'Proof of Registration Fee Payment', field: 'membership_fee_proof_url', required: true },
];

const optionalDocs: DocUpload[] = [
  { label: 'Recommendation Letter', field: 'recommendation_letter_url', required: false, description: 'From bank, contractor, or professional body' },
  { label: 'Financial Statements / Bank Reference', field: 'financial_statement_url', required: false },
  { label: 'Tax Clearance Certificate', field: 'tax_clearance_url', required: false },
];

export default function DeveloperApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [website, setWebsite] = useState('');
  const [establishedYear, setEstablishedYear] = useState('');
  const [description, setDescription] = useState('');

  const [docs, setDocs] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    navigate('/login');
    return null;
  }

  const uploadDoc = async (file: File, field: string) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({ variant: 'destructive', title: 'Invalid file', description: 'Only PDF, JPG, PNG, WEBP allowed' });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ variant: 'destructive', title: 'File too large', description: 'Max 15MB per file' });
      return;
    }

    setUploading(prev => ({ ...prev, [field]: true }));
    const ext = file.name.split('.').pop() || 'pdf';
    const path = `${user.id}/${field}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('developer-documents').upload(path, file, { cacheControl: '3600' });
    
    if (error) {
      toast({ variant: 'destructive', title: 'Upload failed', description: error.message });
    } else {
      // Store the path (not public URL since bucket is private)
      setDocs(prev => ({ ...prev, [field]: path }));
    }
    setUploading(prev => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async () => {
    if (!companyName.trim()) {
      toast({ variant: 'destructive', title: 'Company name required' });
      return;
    }
    // Check required docs
    const requiredFields = [...legalDocs, ...idDocs, ...feeDocs].filter(d => d.required).map(d => d.field);
    const missing = requiredFields.filter(f => !docs[f]);
    if (missing.length > 0) {
      toast({ variant: 'destructive', title: 'Missing required documents', description: `Please upload all required documents (${missing.length} remaining)` });
      return;
    }

    setSubmitting(true);
    const { data: insertedApp, error } = await supabase.from('developer_applications' as any).insert({
      user_id: user.id,
      company_name: companyName,
      phone: phone || null,
      email: email || null,
      address: address || null,
      city: city || null,
      website: website || null,
      established_year: establishedYear ? Number(establishedYear) : null,
      description: description || null,
      ...Object.fromEntries(Object.entries(docs).map(([k, v]) => [k, v])),
    }).select('id').single();
    setSubmitting(false);

    if (error) {
      toast({ variant: 'destructive', title: 'Submission failed', description: error.message });
    } else {
      // Notify reviewers (superadmins + sales team)
      const { data: reviewers } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ['superadmin', 'sales_manager', 'sales_team_leader', 'sales_agent'] as any);
      if (reviewers?.length) {
        await supabase.from('notifications').insert(
          reviewers.map((r: any) => ({
            user_id: r.user_id,
            title: 'New developer application',
            message: `${companyName} submitted a developer application for review.`,
            type: 'application_submitted',
            metadata: { application_id: (insertedApp as any)?.id, application_type: 'developer' },
          }))
        );
      }
      toast({ title: 'Application Submitted!', description: 'Our team will review your application and get back to you.' });
      navigate('/dashboard');
    }
  };

  const renderDocSection = (title: string, icon: React.ReactNode, docList: DocUpload[]) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">{icon} {title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {docList.map(doc => (
          <div key={doc.field} className="flex items-center gap-3 p-3 border rounded-lg">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {doc.label} {doc.required && <span className="text-destructive">*</span>}
              </div>
              {doc.description && <div className="text-xs text-muted-foreground">{doc.description}</div>}
            </div>
            {docs[doc.field] ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                <span className="hidden sm:inline">Uploaded</span>
              </div>
            ) : uploading[doc.field] ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <label className="cursor-pointer">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors">
                  <Upload className="h-3.5 w-3.5" /> Upload
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) uploadDoc(f, doc.field);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="container max-w-3xl py-10">
        <div className="text-center mb-8">
          <Building2 className="h-12 w-12 mx-auto text-primary mb-3" />
          <h1 className="font-display text-2xl md:text-3xl font-bold">Developer Registration</h1>
          <p className="text-muted-foreground mt-2">
            Submit your application to become a verified developer on EthioRealEstates.
            Our team will review your documents and approve your account.
          </p>
        </div>

        <div className="space-y-6">
          {/* Company Profile */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-primary" /> Company Profile
              </CardTitle>
              <CardDescription>Basic information about your company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name <span className="text-destructive">*</span></Label>
                  <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Your company name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="company@email.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+251..." />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Addis Ababa" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Address</Label>
                  <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Office address" />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Established Year</Label>
                  <Input type="number" value={establishedYear} onChange={e => setEstablishedYear(e.target.value)} placeholder="2010" />
                </div>
              </div>
              <div>
                <Label>Company Background & Description</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Brief description of your company, past projects, expertise..." />
              </div>
            </CardContent>
          </Card>

          {/* Document Sections */}
          {renderDocSection('Legal Registration Documents', <FileText className="h-5 w-5 text-primary" />, legalDocs)}
          {renderDocSection('Real Estate Developer Credentials', <FileText className="h-5 w-5 text-primary" />, credentialDocs)}
          {renderDocSection('Identification Documents', <User className="h-5 w-5 text-primary" />, idDocs)}
          {renderDocSection('Membership Fees', <CreditCard className="h-5 w-5 text-primary" />, feeDocs)}
          {renderDocSection('Optional / Supporting Documents', <FolderOpen className="h-5 w-5 text-primary" />, optionalDocs)}

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="gap-2 min-w-[200px]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
